import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import {
  eurosToMicros,
  formatCents,
  useAdminBillingPrices,
  useAdminBillingUsage,
  useSimulateAdminBilling,
} from '../../hooks/use-admin-billing'
import { useAdminOrganizations } from '../../hooks/useQueries'

interface Row {
  code: string
  price: string // euros
  period: 'month' | 'year'
  included: string
  curve: string[]
}

const selectClass = 'bg-background border-input h-9 rounded-md border px-3 text-sm'

function curveOf(runs: Array<[number, number]>): string[] {
  return runs.flatMap(([value, n]) => Array.from({ length: n }, () => String(value)))
}

function startMonthDefault(): string {
  return `${new Date().getUTCFullYear()}-01`
}

/**
 * BillingSimulator: what a plan would cost over a usage curve (spec
 * 2026-10-09-02). The server replays the curve through the real closing on a
 * scratch organization and rolls everything back: nothing is written.
 */
export function BillingSimulator() {
  const { t, i18n } = useTranslation('admin')
  const lang = i18n.language.startsWith('fr') ? 'fr' : 'en'
  const nf = new Intl.NumberFormat(i18n.language)
  const { data: usage } = useAdminBillingUsage()
  const { data: prices } = useAdminBillingPrices()
  const { data: orgs } = useAdminOrganizations({ limit: 100 })
  const simulate = useSimulateAdminBilling()
  const copy = useSimulateAdminBilling()

  const metrics = (usage?.metrics ?? []).filter((m) => m.aggregation === 'max')
  const labelOf = (code: string) => {
    const m = metrics.find((x) => x.code === code)
    return m ? (m.label[lang] ?? m.label.en) : code
  }
  const listPrice = (code: string) => {
    const p = prices?.in_effect.find((x) => x.code === code)
    return p && p.source !== 'none' ? { price: String(p.unit_price_micros / 1_000_000), period: p.period } : null
  }

  const [plan, setPlan] = useState<'monthly' | 'annual'>('annual')
  const [start, setStart] = useState(startMonthDefault())
  const [num, setNum] = useState('10')
  const [den, setDen] = useState('12')
  const [vat, setVat] = useState('20')
  const [months, setMonths] = useState(12)
  const [rows, setRows] = useState<Row[]>([
    { code: 'units_total', price: '', period: 'year', included: '', curve: Array.from({ length: 12 }, () => '0') },
  ])
  const [orgUid, setOrgUid] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const patchRow = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)))

  const setCode = (i: number, code: string) => {
    const lp = listPrice(code)
    patchRow(i, { code, ...(lp ?? {}) })
  }
  const addMonth = () => {
    setMonths((n) => n + 1)
    setRows((rs) => rs.map((r) => ({ ...r, curve: [...r.curve, r.curve[r.curve.length - 1] ?? '0'] })))
  }
  const addCode = () => {
    const used = new Set(rows.map((r) => r.code))
    const code = metrics.find((m) => !used.has(m.code))?.code
    if (!code) return
    const lp = listPrice(code)
    setRows((rs) => [
      ...rs,
      { code, price: lp?.price ?? '', period: lp?.period ?? 'year', included: '', curve: Array.from({ length: months }, () => '0') },
    ])
  }
  const removeRow = (i: number) => setRows((rs) => rs.filter((_, j) => j !== i))

  const preset = (p: 'annual' | 'monthly') => {
    setPlan(p)
    setNum('10')
    setDen('12')
    setMonths(12)
    setRows([
      { code: 'units_total', price: '10', period: 'year', included: '5000', curve: curveOf([[5000, 5], [7000, 3], [4000, 4]]) },
    ])
    simulate.reset()
  }

  const body = () => {
    const priceRows = []
    const includedRows = []
    const curves: Array<Record<string, number>> = Array.from({ length: months }, () => ({}))
    for (const r of rows) {
      const micros = r.price.trim() === '' ? 0 : eurosToMicros(r.price)
      if (micros === null) return null
      priceRows.push({ code: r.code, unit_price_micros: micros, period: r.period })
      if (r.included.trim() !== '') {
        if (!/^\d+$/.test(r.included.trim())) return null
        includedRows.push({ code: r.code, quantity: Number(r.included) })
      }
      for (let m = 0; m < months; m++) {
        const v = r.curve[m]?.trim() ?? ''
        if (!/^\d+$/.test(v)) return null
        curves[m][r.code] = Number(v)
      }
    }
    const n = Number(num)
    const d = Number(den)
    if (!/^\d+$/.test(num) || !/^\d+$/.test(den) || n === 0 || d === 0 || !/^\d+(\.\d+)?$/.test(vat)) return null
    return {
      plan,
      from: `${start}-01`,
      discount_num: n,
      discount_den: d,
      vat_rate_bp: Math.round(Number(vat) * 100),
      payment_terms_days: 30,
      prices: priceRows,
      included: includedRows,
      months: curves,
    }
  }

  const run = () => {
    const b = body()
    if (!b || !/^\d{4}-\d{2}$/.test(start)) {
      setFormError(t('billing.simulator.invalid'))
      return
    }
    setFormError(null)
    simulate.mutate({ body: b }, { onError: () => setFormError(t('billing.simulator.run_error')) })
  }

  const applyOrg = () => {
    if (!orgUid || !/^\d{4}-\d{2}$/.test(start)) return
    copy.mutate(
      { body: { plan, from: `${start}-01`, months: [], from_organization_uid: orgUid, month_count: months, prices: [], included: [] } },
      {
        onSuccess: (res) => {
          setRows((rs) => {
            const next = rs.map((r) => ({ ...r, curve: res.curves.map((c) => String(c[r.code] ?? 0)) }))
            const known = new Set(next.map((r) => r.code))
            for (const code of new Set(res.curves.flatMap((c) => Object.keys(c)))) {
              if (known.has(code) || !res.curves.some((c) => (c[code] ?? 0) > 0)) continue
              const lp = listPrice(code)
              next.push({ code, price: lp?.price ?? '', period: lp?.period ?? 'year', included: '', curve: res.curves.map((c) => String(c[code] ?? 0)) })
            }
            return next
          })
          simulate.reset()
        },
        onError: () => setFormError(t('billing.simulator.copy_error')),
      },
    )
  }

  const result = simulate.data
  const kindTotal = (m: { invoices: Array<{ kind: string; total_excl_vat_cents: number }> }, kind: string) =>
    m.invoices.filter((i) => i.kind === kind).reduce((s, i) => s + i.total_excl_vat_cents, 0)
  let running = 0

  return (
    <div className="space-y-6" data-testid="billing-simulator">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => preset('annual')} data-testid="sim-preset-annual">
          {t('billing.simulator.preset_annual')}
        </Button>
        <Button variant="outline" onClick={() => preset('monthly')} data-testid="sim-preset-monthly">
          {t('billing.simulator.preset_monthly')}
        </Button>
      </div>

      <div className="bg-card rounded-lg border border-border p-4 flex flex-wrap items-end gap-3" data-testid="sim-form">
        <div className="space-y-1">
          <Label htmlFor="sim-plan">{t('billing.simulator.plan')}</Label>
          <select id="sim-plan" className={selectClass} value={plan} onChange={(e) => setPlan(e.target.value as 'monthly' | 'annual')} data-testid="sim-plan">
            <option value="monthly">{t('billing.simulator.plan_monthly')}</option>
            <option value="annual">{t('billing.simulator.plan_annual')}</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="sim-start">{t('billing.simulator.start')}</Label>
          <Input id="sim-start" type="month" value={start} onChange={(e) => setStart(e.target.value)} data-testid="sim-start" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sim-num">{t('billing.simulator.discount')}</Label>
          <div className="flex items-center gap-1">
            <Input id="sim-num" className="w-16" value={num} onChange={(e) => setNum(e.target.value)} data-testid="sim-discount-num" />
            <span>/</span>
            <Input className="w-16" value={den} onChange={(e) => setDen(e.target.value)} data-testid="sim-discount-den" />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="sim-vat">{t('billing.simulator.vat')}</Label>
          <Input id="sim-vat" className="w-20" value={vat} onChange={(e) => setVat(e.target.value)} data-testid="sim-vat" />
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-4 flex flex-wrap items-end gap-3" data-testid="sim-copy">
        <div className="space-y-1">
          <Label htmlFor="sim-org">{t('billing.simulator.copy_from')}</Label>
          <select id="sim-org" className={selectClass} value={orgUid} onChange={(e) => setOrgUid(e.target.value)} data-testid="sim-copy-org">
            <option value="">{t('billing.simulator.copy_pick')}</option>
            {(orgs?.data ?? []).map((o) => (
              <option key={o.uid} value={o.uid}>
                {o.name || o.code}
              </option>
            ))}
          </select>
        </div>
        <Button variant="outline" onClick={applyOrg} disabled={!orgUid || copy.isPending} data-testid="sim-copy-apply">
          {t('billing.simulator.copy_apply')}
        </Button>
        <p className="text-sm text-muted-foreground">{t('billing.simulator.copy_help', { months })}</p>
      </div>

      <div className="space-y-4" data-testid="sim-rows">
        {rows.map((r, i) => (
          <div key={i} className="bg-card rounded-lg border border-border p-4 space-y-3" data-testid={`sim-row-${i}`}>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label>{t('billing.simulator.code')}</Label>
                <select className={selectClass} value={r.code} onChange={(e) => setCode(i, e.target.value)} data-testid={`sim-row-${i}-code`}>
                  {metrics.map((m) => (
                    <option key={m.code} value={m.code}>
                      {labelOf(m.code)}
                    </option>
                  ))}
                  {!metrics.some((m) => m.code === r.code) && <option value={r.code}>{r.code}</option>}
                </select>
              </div>
              <div className="space-y-1">
                <Label>{t('billing.simulator.price')}</Label>
                <Input className="w-28" value={r.price} onChange={(e) => patchRow(i, { price: e.target.value })} data-testid={`sim-row-${i}-price`} />
              </div>
              <div className="space-y-1">
                <Label>{t('billing.simulator.period')}</Label>
                <select className={selectClass} value={r.period} onChange={(e) => patchRow(i, { period: e.target.value as 'month' | 'year' })} data-testid={`sim-row-${i}-period`}>
                  <option value="month">{t('billing.simulator.per_month')}</option>
                  <option value="year">{t('billing.simulator.per_year')}</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>{t('billing.simulator.included')}</Label>
                <Input className="w-28" value={r.included} onChange={(e) => patchRow(i, { included: e.target.value })} data-testid={`sim-row-${i}-included`} />
              </div>
              {rows.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => removeRow(i)} data-testid={`sim-row-${i}-remove`}>
                  {t('billing.simulator.remove')}
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: months }, (_, m) => (
                <div key={m} className="space-y-1">
                  <span className="block text-xs text-muted-foreground">{t('billing.simulator.month_n', { n: m + 1 })}</span>
                  <Input
                    className="w-20"
                    value={r.curve[m] ?? ''}
                    onChange={(e) => patchRow(i, { curve: r.curve.map((v, k) => (k === m ? e.target.value : v)) })}
                    data-testid={`sim-row-${i}-month-${m + 1}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={addMonth} data-testid="sim-add-month">
            {t('billing.simulator.add_month')}
          </Button>
          <Button variant="outline" onClick={addCode} data-testid="sim-add-code">
            {t('billing.simulator.add_code')}
          </Button>
          <Button onClick={run} disabled={simulate.isPending} data-testid="sim-run">
            {t('billing.simulator.run')}
          </Button>
        </div>
        {formError && (
          <p className="text-sm text-destructive" data-testid="sim-error">
            {formError}
          </p>
        )}
      </div>

      {result && (
        <div className="space-y-3" data-testid="sim-result">
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('billing.simulator.col_month')}</TableHead>
                  <TableHead className="text-right">{t('billing.simulator.col_quantity')}</TableHead>
                  <TableHead className="text-right">{t('billing.simulator.col_monthly')}</TableHead>
                  <TableHead className="text-right">{t('billing.simulator.col_annual')}</TableHead>
                  <TableHead className="text-right">{t('billing.simulator.col_overage')}</TableHead>
                  <TableHead className="text-right">{t('billing.simulator.col_running')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.months.map((m, i) => {
                  const monthly = kindTotal(m, 'monthly')
                  const annual = kindTotal(m, 'annual')
                  const overage = kindTotal(m, 'annual_overage')
                  running += monthly + annual + overage
                  return (
                    <TableRow key={m.month} data-testid={`sim-month-${m.month}`}>
                      <TableCell>{m.month}</TableCell>
                      <TableCell className="text-right tabular-nums" data-testid={`sim-month-${m.month}-quantity`}>
                        {Object.entries(result.curves[i] ?? {})
                          .map(([c, q]) => `${labelOf(c)}: ${nf.format(q)}`)
                          .join(', ')}
                      </TableCell>
                      <TableCell className="text-right tabular-nums" data-testid={`sim-month-${m.month}-monthly`}>{formatCents(monthly, i18n.language)}</TableCell>
                      <TableCell className="text-right tabular-nums" data-testid={`sim-month-${m.month}-annual`}>{formatCents(annual, i18n.language)}</TableCell>
                      <TableCell className="text-right tabular-nums" data-testid={`sim-month-${m.month}-overage`}>{formatCents(overage, i18n.language)}</TableCell>
                      <TableCell className="text-right tabular-nums" data-testid={`sim-month-${m.month}-running`}>{formatCents(running, i18n.language)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
          <dl className="grid grid-cols-[auto_auto] justify-start gap-x-6 gap-y-1 text-sm">
            <dt>{t('billing.simulator.total_excl')}</dt>
            <dd className="tabular-nums" data-testid="sim-total-excl">{formatCents(result.total_excl_vat_cents, i18n.language)}</dd>
            <dt>{t('billing.simulator.total_vat')}</dt>
            <dd className="tabular-nums" data-testid="sim-total-vat">{formatCents(result.total_incl_vat_cents - result.total_excl_vat_cents, i18n.language)}</dd>
            <dt>{t('billing.simulator.total_incl')}</dt>
            <dd className="tabular-nums" data-testid="sim-total-incl">{formatCents(result.total_incl_vat_cents, i18n.language)}</dd>
            <dt>{t('billing.simulator.average')}</dt>
            <dd className="tabular-nums" data-testid="sim-average">{formatCents(Math.round(result.total_excl_vat_cents / Math.max(result.months.length, 1)), i18n.language)}</dd>
          </dl>
          <p className="text-xs text-muted-foreground">{t('billing.simulator.excl_note')}</p>
        </div>
      )}
    </div>
  )
}
